<script setup lang="ts">
  import { computed } from 'vue';
  import type { TrainingDetail, TrainingDetailTrainee } from '@/entities/Training';
  import { formatCivilDateToPtBR } from '@/shared/lib';
  import { formatTrainingControlValue } from '../../lib/formatTrainingControlValue';
  import { formatTrainingMatrixRevisionShortLabel } from '../../lib/formatTrainingMatrixRevisionShortLabel';

  const props = defineProps<{
    scheduling: TrainingDetail['scheduling'];
    trainees: TrainingDetailTrainee[];
    matrixRevision: TrainingDetail['matrixRevision'];
  }>();

  const scheduledDateLabel = computed(() =>
    props.scheduling.date
      ? formatCivilDateToPtBR(props.scheduling.date)
      : formatTrainingControlValue(null)
  );

  const traineeRows = computed<(TrainingDetailTrainee | null)[]>(() =>
    props.trainees.length > 0 ? props.trainees : [null]
  );

  const matrixRevisionLabel = computed(() =>
    formatTrainingMatrixRevisionShortLabel(props.matrixRevision)
  );
</script>

<template>
  <section
    data-testid="training-control-section-scheduling"
    class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8"
  >
    <h2 class="text-sm font-semibold text-gray-07">
      Programação do Treinamento
    </h2>

    <div class="grid gap-4 md:grid-cols-3">
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold text-gray-07">Responsável pela Programação:</span>
        <span
          data-testid="training-control-scheduling-responsible"
          class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
        >
          {{ formatTrainingControlValue(scheduling.responsible?.name) }}
        </span>
      </div>
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold text-gray-07">Cargo:</span>
        <span
          data-testid="training-control-scheduling-position"
          class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
        >
          {{ formatTrainingControlValue(scheduling.position?.name) }}
        </span>
      </div>
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold text-gray-07">Data da programação:</span>
        <span
          data-testid="training-control-scheduling-date"
          class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
        >
          {{ scheduledDateLabel }}
        </span>
      </div>
    </div>

    <div
      v-for="(trainee, index) in traineeRows"
      :key="trainee?.id ?? 'sem-colaborador'"
      data-testid="training-control-trainee-row"
      class="grid gap-4 md:grid-cols-3"
    >
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold text-gray-07">Colaborador a ser treinado</span>
        <span
          data-testid="training-control-trainee-name"
          class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
        >
          {{ formatTrainingControlValue(trainee?.name) }}
        </span>
      </div>
      <div class="flex flex-col gap-2">
        <span class="text-xs font-semibold text-gray-07">Cargo:</span>
        <span
          data-testid="training-control-trainee-position"
          class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
        >
          {{ formatTrainingControlValue(trainee?.position?.name) }}
        </span>
      </div>
      <div
        v-if="index === 0"
        class="flex flex-col gap-2"
      >
        <span class="text-xs font-semibold text-gray-07">Matriz do treinamento:</span>
        <span
          data-testid="training-control-matrix-revision"
          class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
        >
          {{ matrixRevisionLabel }}
        </span>
      </div>
    </div>
  </section>
</template>
